import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'

import { ChromePrimaryButton } from '../ChromeActions'

export type AgentQuestionOption = {
  value: string
  label: string
  description?: string
}

export type AgentQuestion = {
  question: string
  options: AgentQuestionOption[]
  /** 多选题：可勾选多个选项，自定义文本作为补充信息一并提交；缺省为单选 */
  multiSelect?: boolean
}

type AgentQuestionOverlayProps = {
  angular: boolean
  /** 提问列表：多条时右上角分步切换，逐条作答后统一提交 */
  questions: AgentQuestion[]
  /** 提交：answers 与 questions 一一对应（已翻译为可读文本：选项 label / 自定义文本 / 多选拼接） */
  onSubmit: (answers: string[]) => void
  /** 右上角关闭 = 用户放弃回答 */
  onClose: () => void
}

const dropdownTrans = { type: 'spring', stiffness: 420, damping: 32, mass: 0.7 } as const

/**
 * 模型向用户提问的底部覆盖层（ask_user 工具驱动）：问题 + 选项按钮 + 自定义回答。
 *
 * 交互约定：
 * - 单选题：点击选项即选中并自动跳到下一题（最后一题不跳，留在原地供补充或确认）；
 *   若填了自定义回答，提交时优先使用自定义（自定义视为对候选项的否定/替代）。
 * - 多选题（multiSelect）：点击切换勾选，不自动跳转；自定义文本作为补充信息与选项一起提交。
 * - 回答有效性：选中选项或填了自定义任一即可——只填自定义也允许提交。
 * - 底部按钮：非最后一题为「下一条」（当前题已作答才可用），最后一题为「提交」（全部作答才可用）；
 *   右上角 ‹ n/N › 切换问题，× 关闭视为用户未回答。
 */
export function AgentQuestionOverlay({ angular, questions, onSubmit, onClose }: AgentQuestionOverlayProps) {
  const reducedMotion = useReducedMotion()
  const [step, setStep] = useState(0)
  const [picked, setPicked] = useState<Record<number, string[]>>({})
  const [customs, setCustoms] = useState<Record<number, string>>({})
  const total = questions.length
  const current = questions[Math.min(step, total - 1)]
  const currentPicked = picked[step] ?? []
  const currentCustom = customs[step] ?? ''
  const isLast = step >= total - 1
  const round = angular ? 'rounded-none' : 'rounded-2xl'

  const isAnswered = (index: number) =>
    (picked[index]?.length ?? 0) > 0 || Boolean((customs[index] ?? '').trim())
  const allAnswered = questions.every((_, index) => isAnswered(index))
  const currentAnswered = isAnswered(step)

  /** 单题的最终回答文本：选项 label 与自定义按题型的组合规则拼接 */
  const buildAnswer = (index: number): string => {
    const labels = (picked[index] ?? []).map(
      (value) => questions[index]?.options.find((option) => option.value === value)?.label ?? value,
    )
    const custom = (customs[index] ?? '').trim()
    if (questions[index]?.multiSelect) {
      // 多选：选项 + 自定义补充一起带上
      return [...labels, custom].filter(Boolean).join('；')
    }
    // 单选：自定义优先（用户明确输入视为替代答案）
    return custom || labels[0] || ''
  }

  const selectOption = (value: string) => {
    if (current.multiSelect) {
      setPicked((prev) => {
        const existing = prev[step] ?? []
        return {
          ...prev,
          [step]: existing.includes(value) ? existing.filter((item) => item !== value) : [...existing, value],
        }
      })
      return
    }
    // 单选：选中即记忆；非最后一题自动跳下一条（跳转保留已填自定义，不清空）
    setPicked((prev) => ({ ...prev, [step]: [value] }))
    if (!isLast) setStep((prev) => Math.min(prev + 1, total - 1))
  }

  const goNext = () => {
    if (!currentAnswered) return
    if (!isLast) setStep((prev) => Math.min(prev + 1, total - 1))
  }

  const submitAll = () => {
    if (!allAnswered) return
    onSubmit(questions.map((_, index) => buildAnswer(index)))
  }

  const handleEnter = () => {
    if (isLast) {
      if (allAnswered) submitAll()
      return
    }
    goNext()
  }

  if (!current) return null

  return (
    <AnimatePresence>
      <motion.div
        key="agent-question-overlay"
        className="absolute inset-x-0 bottom-0 z-30 px-3 pb-3 sm:px-5 sm:pb-4 lg:px-6"
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
        transition={dropdownTrans}
      >
        <div
          role="dialog"
          aria-label="模型提问"
          className={[
            'mx-auto w-full max-w-3xl overflow-hidden bg-[color-mix(in_srgb,var(--menu-solid)_96%,transparent)] shadow-float ring-1 ring-[color-mix(in_srgb,var(--line)_35%,transparent)]',
            round,
          ].join(' ')}
        >
          {/* 头部：标签 + 问题分步切换 + 关闭 */}
          <div className="flex items-center gap-2 px-4 pt-3">
            <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" />
            <p className="min-w-0 flex-1 truncate text-[11px] font-medium tracking-[0.08em] text-ink-soft">
              {total > 1 ? `模型向你提问 · 第 ${step + 1}/${total} 问` : '模型向你提问'}
            </p>
            {total > 1 ? (
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  aria-label="上一个问题"
                  disabled={step <= 0}
                  className="rounded-md p-1 text-ink-soft transition hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-ink disabled:opacity-30"
                  onClick={() => setStep((prev) => Math.max(0, prev - 1))}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <span className="min-w-[2.5rem] text-center text-[11px] tabular-nums text-ink-soft">
                  {step + 1}/{total}
                </span>
                <button
                  type="button"
                  aria-label="下一个问题"
                  disabled={step >= total - 1}
                  className="rounded-md p-1 text-ink-soft transition hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-ink disabled:opacity-30"
                  onClick={() => setStep((prev) => Math.min(total - 1, prev + 1))}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            ) : null}
            <button
              type="button"
              aria-label="关闭提问（不回答）"
              className="shrink-0 rounded-md p-1 text-ink-soft transition hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] hover:text-ink"
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <p className="px-4 pt-1.5 text-[13px] leading-relaxed text-ink">
            {current.question}
            {current.multiSelect ? (
              <span className="ml-1.5 align-middle text-[11px] text-ink-soft">（可多选）</span>
            ) : null}
          </p>

          {current.options.length > 0 ? (
            <div className="flex flex-col gap-1.5 px-4 pt-3">
              {current.options.map((option) => {
                const isActive = currentPicked.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={isActive}
                    className={[
                      'w-full px-3 py-2 text-left transition',
                      round,
                      isActive
                        ? 'bg-[color-mix(in_srgb,var(--accent)_16%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--accent)_45%,transparent)]'
                        : 'bg-[color-mix(in_srgb,var(--surface-muted)_65%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--line)_30%,transparent)] hover:bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]',
                    ].join(' ')}
                    onClick={() => selectOption(option.value)}
                  >
                    <span className="block text-[13px] font-medium text-ink">{option.label}</span>
                    {option.description ? (
                      <span className="mt-0.5 block text-[11px] leading-snug text-ink-soft">
                        {option.description}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          ) : null}

          {/* 底部：自定义回答输入 + 下一条/提交 */}
          <div className="flex items-center gap-2 px-4 pb-3 pt-3">
            <input
              value={currentCustom}
              onChange={(event) => setCustoms((prev) => ({ ...prev, [step]: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleEnter()
                }
              }}
              placeholder={
                current.multiSelect
                  ? '可补充说明，会随勾选的选项一起提交…'
                  : '或输入自定义回答（填写后优先使用自定义）…'
              }
              className={[
                'min-w-0 flex-1 bg-[color-mix(in_srgb,var(--surface-muted)_75%,transparent)] px-3 py-2 text-[13px] text-ink outline-none ring-1 ring-[color-mix(in_srgb,var(--line)_35%,transparent)] transition placeholder:text-ink-soft focus:ring-[color-mix(in_srgb,var(--accent)_45%,transparent)]',
                angular ? 'rounded-none' : 'rounded-lg',
              ].join(' ')}
            />
            {isLast ? (
              <ChromePrimaryButton
                type="button"
                density="compact"
                disabled={!allAnswered}
                title={allAnswered ? undefined : '还有未回答的问题（可用右上角箭头切换）'}
                onClick={submitAll}
              >
                提交
              </ChromePrimaryButton>
            ) : (
              <ChromePrimaryButton
                type="button"
                density="compact"
                disabled={!currentAnswered}
                title={currentAnswered ? undefined : '先选择一个选项或输入自定义回答'}
                onClick={goNext}
              >
                下一条
              </ChromePrimaryButton>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
